/**
 * Hourly cloud cover from Open-Meteo (MET Nordic, `metno_seamless`) — the one thing the seNorge
 * grids don't have, needed to tell clear nights (surface hoar, facets, sunny melt) from cloudy
 * ones. Called directly from the browser (CORS `*`, no key; free for non-commercial use, data
 * CC BY 4.0).
 */

const DAY_MS = 86_400_000;

/**
 * Cloud cover (0–100 %) for each hour in [start, start + hours), UTC; null entries where
 * missing. Recent dates come from the forecast API (it keeps ~3 months), older ones from the
 * historical-forecast archive.
 */
export async function fetchCloudCover(
  lat: number,
  lon: number,
  start: Date,
  hours: number,
  signal?: AbortSignal,
): Promise<(number | null)[]> {
  const end = new Date(start.getTime() + hours * 3_600_000);
  const recent = Date.now() - start.getTime() < 80 * DAY_MS;
  const host = recent ? 'api.open-meteo.com' : 'historical-forecast-api.open-meteo.com';
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    hourly: 'cloud_cover',
    models: 'metno_seamless',
    timezone: 'UTC',
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10),
  });
  const res = await fetch(`https://${host}/v1/forecast?${params}`, signal ? { signal } : {});
  if (!res.ok) throw new Error(`Open-Meteo: HTTP ${res.status}`);
  const json = (await res.json()) as {
    hourly?: { time: string[]; cloud_cover: (number | null)[] };
  };
  const byTime = new Map<number, number | null>();
  json.hourly?.time.forEach((t, k) => {
    byTime.set(Date.parse(`${t}:00Z`), json.hourly!.cloud_cover[k] ?? null);
  });
  const out: (number | null)[] = [];
  for (let h = 0; h < hours; h++) out.push(byTime.get(start.getTime() + h * 3_600_000) ?? null);
  return out;
}
